import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, Tooltip } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import {
  useGetAiModelTasks,
  useGetAiModels,
  useGetAiGuardrails,
  useInsertAiModelTask,
  useUpdateAiModelTask,
  useDeleteAiModelTask
} from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import { useListStyles } from './useListStyles'
import { TASK_FIELDS } from './fieldSchemas'
import { useToast } from './ToastContext'
import RecordDrawer from './RecordDrawer'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'

function TasksTab ({ modelFilter, setModelFilter }) {
  const classes = useListStyles()
  const { showToast, showError } = useToast()
  const { data, isLoading } = useGetAiModelTasks()
  const { data: modelsData } = useGetAiModels({ admin: true })
  const { data: guardrailsData } = useGetAiGuardrails()
  const insertMutation = useInsertAiModelTask()
  const updateMutation = useUpdateAiModelTask()
  const deleteMutation = useDeleteAiModelTask()

  const [searchText, setSearchText] = useState('')
  const [drawer, setDrawer] = useState({ open: false, record: null })
  const [confirm, setConfirm] = useState(null)

  const models = useMemo(() => modelsData?.models || [], [modelsData])
  const guardrails = useMemo(() => guardrailsData?.guardrails || [], [guardrailsData])
  const modelLabelById = useMemo(() => new Map(models.map(m => [m.modelId, m.name || m.modelId])), [models])
  const guardrailLabelById = useMemo(() => new Map(guardrails.map(g => [g.guardrailId, g.name || g.guardrailId])), [guardrails])

  const tableData = useMemo(() => {
    const tasks = data?.tasks || []
    return tasks
      .filter(t => !modelFilter || t.model1 === modelFilter || t.model2 === modelFilter || t.model3 === modelFilter)
      .map(t => ({
        ...t,
        model1Label: modelLabelById.get(t.model1) || t.model1,
        model2Label: t.model2 ? (modelLabelById.get(t.model2) || t.model2) : null,
        model3Label: t.model3 ? (modelLabelById.get(t.model3) || t.model3) : null,
        guardrailLabel: t.guardrailId ? (guardrailLabelById.get(t.guardrailId) || t.guardrailId) : null,
        lastModified: t.modifiedDate || t.createdDate || null,
        lastModifiedBy: t.modifiedBy || t.createdBy || null
      }))
  }, [data, modelFilter, modelLabelById, guardrailLabelById])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'taskCode',
      accessor: 'taskCode',
      Header: 'Code',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'name', accessor: 'name', Header: 'Name' },
    {
      id: 'model1',
      accessor: 'model1Label',
      Header: 'Primary',
      Cell: ({ row }) => <span className={classes.mono}>{row.original.model1Label}</span>
    },
    {
      id: 'model2',
      accessor: 'model2Label',
      Header: 'Fallback 1',
      Cell: ({ row }) => row.original.model2Label
        ? <span className={classes.mono}>{row.original.model2Label}</span>
        : <span className={classes.timestampText}>—</span>
    },
    {
      id: 'model3',
      accessor: 'model3Label',
      Header: 'Fallback 2',
      Cell: ({ row }) => row.original.model3Label
        ? <span className={classes.mono}>{row.original.model3Label}</span>
        : <span className={classes.timestampText}>—</span>
    },
    {
      id: 'temperature',
      accessor: 'temperature',
      Header: 'Temp',
      Cell: ({ value }) => <span className={classes.mono}>{value == null ? '—' : Number(value).toFixed(2)}</span>
    },
    { id: 'maxTokens', accessor: 'maxTokens', Header: 'Max Tokens' },
    {
      id: 'guardrail',
      accessor: 'guardrailLabel',
      Header: 'Guardrail',
      Cell: ({ row }) => row.original.guardrailLabel
        ? <span className={classes.mono}>{row.original.guardrailLabel}</span>
        : <span className={classes.timestampText}>—</span>
    },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          <Tooltip title='Edit task' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setDrawer({ open: true, record: row.original })}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete task' arrow>
            <IconButton
              size='small'
              className={classes.dangerBtn}
              onClick={() => setConfirm({ taskCode: row.original.taskCode })}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'taskCode', desc: false }], [])

  // Admins can wire a task to any model — including hidden or disabled ones —
  // since they may be staging configuration before flipping flags. Annotate
  // the dropdown labels so the status is visible at point of selection.
  const drawerOptions = useMemo(() => {
    const annotate = (m) => {
      const flags = []
      if (!m.enabled) flags.push('disabled')
      if (m.hidden) flags.push('hidden')
      const base = m.name || m.modelId
      return flags.length ? `${base} (${flags.join(', ')})` : base
    }
    return {
      models: models.map(m => ({ value: m.modelId, label: annotate(m) })),
      guardrails: guardrails.map(g => ({ value: g.guardrailId, label: g.name || g.guardrailId }))
    }
  }, [models, guardrails])

  const handleDelete = useCallback(async () => {
    if (!confirm) return
    try {
      await deleteMutation.mutateAsync({ taskCode: confirm.taskCode })
      showToast('Task deleted')
    } catch (err) {
      showError(err, 'Delete failed')
    } finally {
      setConfirm(null)
    }
  }, [confirm, deleteMutation, showToast, showError])

  const isMutating = insertMutation.isLoading || updateMutation.isLoading
  const openNew = () => setDrawer({ open: true, record: null })
  const allCount = (data?.tasks || []).length

  return (
    <>
      <div className={classes.newBtnWrap}>
        <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Task</SydButton>
      </div>

      {isLoading ? (
        <Loading />
      ) : allCount === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateMessage}>
            No AI tasks yet. Create one to get started.
          </div>
          <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Task</SydButton>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.recordCount}>
              {tableData.length} of {allCount} {allCount === 1 ? 'task' : 'tasks'}
            </span>
            <div className={classes.toolbarRight}>
              <select
                className={classes.filterSelect}
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
              >
                <option value=''>All models</option>
                {models.map(m => <option key={m.modelId} value={m.modelId}>{m.name || m.modelId}</option>)}
              </select>
              <input
                className={classes.searchInput}
                placeholder='Search tasks…'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='AI Tasks'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      <RecordDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, record: null })}
        title={drawer.record ? (drawer.record.name || drawer.record.taskCode) : 'New Task'}
        entityLabel='Task'
        fields={TASK_FIELDS}
        options={drawerOptions}
        record={drawer.record}
        isMutating={isMutating}
        onInsert={(payload) => insertMutation.mutateAsync(payload)}
        onUpdate={(payload) => updateMutation.mutateAsync({ ...payload, taskCode: drawer.record.taskCode })}
      />

      <ConfirmDeleteDialog
        open={!!confirm}
        entityLabel='Task'
        recordId={confirm?.taskCode}
        isMutating={deleteMutation.isLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </>
  )
}

TasksTab.propTypes = {
  modelFilter: PropTypes.string.isRequired,
  setModelFilter: PropTypes.func.isRequired
}

export default TasksTab
